import type { Address, Hex } from 'viem';
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts';

import { AGENT_EXPIRY_BUFFER_MS, HYPERLIQUID_NETWORK } from './constants';

/**
 * A browser-held Hyperliquid API wallet. It can only sign trading actions for
 * the approving user (never withdrawals) and is scoped to one user address on
 * one network. `validUntil` is a local rotation deadline: re-approving under
 * the same agent name deregisters the previous key on Hyperliquid.
 */
export type PerpsAgent = {
  privateKey: Hex;
  address: Address;
  validUntil: number;
};

const storageKey = (user: Address) =>
  `matcha-perps-agent:${HYPERLIQUID_NETWORK}:${user.toLowerCase()}`;

export function createAgent(validUntil: number): PerpsAgent {
  const privateKey = generatePrivateKey();
  return { privateKey, address: privateKeyToAccount(privateKey).address, validUntil };
}

export function loadAgent(user: Address): PerpsAgent | null {
  const key = storageKey(user);
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PerpsAgent;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

export function saveAgent(user: Address, agent: PerpsAgent) {
  localStorage.setItem(storageKey(user), JSON.stringify(agent));
}

export function clearAgent(user: Address) {
  localStorage.removeItem(storageKey(user));
}

export function isAgentUsable(agent: PerpsAgent | null, now = Date.now()): agent is PerpsAgent {
  return agent != null && agent.validUntil - AGENT_EXPIRY_BUFFER_MS > now;
}

export const agentAccount = (agent: PerpsAgent) => privateKeyToAccount(agent.privateKey);
